/**
 * One row shape for both review lanes.
 *
 * An internal review is a `tasks` notification; an external review is a share
 * with approval enabled. They are different objects with different endpoints,
 * but they answer the same six questions — what, who is reviewing, how far
 * along, by when, what state, where do I go — which is why they belong in one
 * table with a type badge rather than in two features. This module is where
 * each is flattened onto that shape; the page itself then knows nothing about
 * notifications or shares.
 *
 * See `docs/design/workflows-section.md`.
 */
import { Share, getShareTitle, getShareAssetCount } from 'src/types/TShare';
import { TNotification, TTaskIntent } from 'src/types/TNotification';
import { taskSubject } from 'src/utils/notificationLink';

export type ReviewLane = 'internal' | 'external';

/** Resolved display state. Deliberately NOT the raw status of either
 *  underlying object: "declined" and "11 rejected" are different words for
 *  the same thing happening, and the list needs one vocabulary. */
export type ReviewState =
  | 'not_opened'   // external: link never followed
  | 'in_review'    // waiting on the reviewer
  | 'approved'     // every asset approved / task completed
  | 'rejected'     // at least one rejection
  | 'declined'     // internal: reviewer refused the task
  | 'dismissed'    // internal: reviewer dropped it
  | 'withdrawn'    // internal: the REQUESTER stopped waiting
  | 'closed';      // external: the owner retired a link nobody opened

export type ReviewRow = {
  key: string;
  lane: ReviewLane;
  title: string;
  /** Second line: what the row is about (asset count, filters, intent). */
  detail: string;
  reviewers: string[];
  /** Decided vs total. `total` 0 means "no progress to show". */
  decided: number;
  rejected: number;
  total: number;
  /** ISO string or null. */
  dueOn: string | null;
  state: ReviewState;
  /** Where clicking the row goes. */
  href: string;
  /** The underlying object, so a row action knows what to call: a task id for
   *  the internal lane, a share id for the external one. */
  refId: string | number;
};

export const INTENT_LABEL: Record<TTaskIntent, string> = {
  approve: 'Check & approve',
  comment: 'Take a look',
  fix: 'Fix & re-upload',
};

export const STATE_LABEL: Record<ReviewState, string> = {
  not_opened: 'Not opened',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  declined: 'Declined',
  dismissed: 'Dismissed',
  withdrawn: 'Withdrawn',
  closed: 'Closed',
};

/**
 * A review nobody is waiting on any more.
 *
 * This is what lets the list stop growing. `not_opened` and `in_review` are
 * live; everything else has an answer, or was retired by someone who decided
 * an answer was no longer coming.
 */
export const isSettled = (row: ReviewRow): boolean =>
  row.state !== 'in_review' && row.state !== 'not_opened';

/** Semantic bucket for the status pill. */
export const stateTone = (state: ReviewState): 'ok' | 'wait' | 'bad' | 'idle' => {
  switch (state) {
    case 'approved':
      return 'ok';
    case 'rejected':
    case 'declined':
      return 'bad';
    // Withdrawn and closed are the requester's own decision, not a verdict
    // that went against them. Colouring them red would report a tidy-up as a
    // problem.
    case 'withdrawn':
    case 'closed':
      return 'idle';
    case 'in_review':
      return 'wait';
    default:
      return 'idle';
  }
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * True when a row is something the requester should ACT on, rather than just
 * wait on. Drives the "Needs attention" tab.
 *
 * Only two things qualify: a verdict came back negative (rejected/declined),
 * or a deadline passed with the review unfinished. A review with no deadline
 * is never overdue, rather than instantly so.
 *
 * `not_opened` deliberately does NOT qualify, even though it is tempting: a
 * review link nobody has opened yet is the normal state of a review sent five
 * minutes ago. Counting it flagged 16 of 19 rows on a real dataset, which
 * makes the tab meaningless and tints the whole table — an alarm that is
 * always on is not an alarm. It stays visible in the Status column, where it
 * belongs.
 */
export const needsAttention = (row: ReviewRow, now: Date = new Date()): boolean => {
  if (row.state === 'rejected' || row.state === 'declined') return true;
  return Boolean(row.dueOn && new Date(row.dueOn) < now && row.decided < row.total);
};

export const fromTask = (n: TNotification): ReviewRow => {
  const intent = n.intent ? INTENT_LABEL[n.intent] : null;
  const reviewer = n.assigned_to_email || '';

  let state: ReviewState;
  switch (n.status) {
    case 'DONE':
      state = 'approved';
      break;
    case 'DECLINED':
      state = 'declined';
      break;
    case 'DISMISSED':
      state = 'dismissed';
      break;
    case 'CANCELLED':
      state = 'withdrawn';
      break;
    default:
      state = 'in_review';
  }

  // An internal task is resolved wholesale — one reviewer, one verdict — so
  // progress is 0/1 or 1/1 rather than a per-asset fraction. Showing it on the
  // same meter as an external review is honest: both read "how much of this is
  // settled", at the granularity the underlying object actually has.
  const decided = state === 'in_review' ? 0 : 1;

  return {
    key: `task-${n.id}`,
    lane: 'internal',
    title: taskSubject(n.message),
    detail: intent ? `${intent} · assigned review` : 'Assigned review',
    reviewers: reviewer ? [reviewer] : [],
    decided,
    rejected: state === 'declined' ? 1 : 0,
    total: 1,
    dueOn: n.due_on,
    state,
    href: n.url || '',
    refId: n.id,
  };
};

export const fromShare = (s: Share): ReviewRow => {
  const total = getShareAssetCount(s);
  const approved = s.approved_count ?? 0;
  const rejected = s.rejected_count ?? 0;
  const decided = approved + rejected;

  let state: ReviewState;
  if ((s.status || '').toUpperCase() === 'CLOSED') {
    // An explicit decision by the owner outranks whatever the per-asset
    // tallies would otherwise say.
    state = 'closed';
  } else if (rejected > 0) {
    state = 'rejected';
  } else if (total > 0 && approved >= total) {
    state = 'approved';
  } else if (decided > 0) {
    state = 'in_review';
  } else {
    // Nothing decided yet. `access_count` would tell us whether the link was
    // ever followed, but the list response does not carry it — so an
    // untouched review reads as "not opened", which is what the requester
    // needs to know either way: nobody has done anything.
    state = 'not_opened';
  }

  return {
    key: `share-${s.id}`,
    lane: 'external',
    title: getShareTitle(s),
    detail: `${plural(total, 'asset')} · review link`,
    // The list response carries no recipient list; ShareDetails does. Rather
    // than N+1 the page to fill one column, the row says how many people
    // could act and defers the names to the share itself.
    reviewers: [],
    decided,
    rejected,
    total,
    dueOn: s.due_on ?? null,
    state,
    href: `/shares/${s.id}`,
    refId: s.id,
  };
};

/** Due soonest first, undated last, then most recently touched. */
export const byUrgency = (a: ReviewRow, b: ReviewRow): number => {
  if (a.dueOn && b.dueOn) return new Date(a.dueOn).getTime() - new Date(b.dueOn).getTime();
  if (a.dueOn) return -1;
  if (b.dueOn) return 1;
  return a.title.localeCompare(b.title);
};
